import useTransformation from '../../../../../../hooks/useTransformation';
import ConditionBody from './ConditionBody';
import { useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useHotkeys } from 'react-hotkeys-hook';
import styled from 'styled-components';
import {
  Flex,
  Select,
  Group,
  Tooltip,
  Text,
  rem,
  Input,
  Button,
  RadioGroup,
  Radio
} from '@mantine/core';

import { cloneDeep } from 'lodash';
import { extractApiError } from '../../../../../../utils';
import {
  fillTypesArray,
  transformationTooltips,
  transportationKeys,
  transportationsArray
} from '../../../../../../consts/conditions';
import { TbInfoCircle } from 'react-icons/tb';
import { FaCheck } from 'react-icons/fa6';
import { IoIosArrowDown } from 'react-icons/io';
import { SearchSelect } from './SearchSelect';

const renderSelectOption = ({ option, checked, hideTooltip }) => {
  return (
    <Group flex="1" gap="xs" justify="space-between">
      <Flex align="center" gap="6px">
        {checked && <FaCheck />}
        {option.label}
      </Flex>
      {!hideTooltip && (
        <Tooltip label={transformationTooltips[option.value]}>
          <Flex>
            <TbInfoCircle fontSize="16px" color="#228BE6" />
          </Flex>
        </Tooltip>
      )}
    </Group>
  );
};

export const TransformationRenderer = ({
  setIsOpen,
  setSelectedTransformation,
  selectedTransformation,
  columns,
  transformationType,
  setTransformationType,
  addTransformation,
  editTransformation,
  setAmongIndex,
  tranformationId,
  extractId,
  periodId,
  workflowId,
  tableName,
  isLoadingTransformationPatch,
  showList = false,
  periodName,
  year,
  isLockedPeriod,
  filterActions,
  onChangeTransformationType,
  disableSelectActionType,
  initialFilterTypes,
  close,
  workflowName
}) => {
  const {
    elseValue,
    filterTypes,
    replaceColumn,
    selectedItems,
    disableSaveBtn,
    disableMessage,
    clear,
    onChangeThen,
    onSelectItem,
    addCondition,
    onChangeKeep,
    onChangeInput,
    handleDragEnd,
    onChangeColumn,
    onClearColumns,
    onClearColumns2,
    onAddListItem,
    onRemoveListItem,
    setFilterTypes,
    onAddFilterType,
    onChangeOperator,
    onChangeFunction,
    onChangeInputType,
    onSelectCondition,
    onSelectFilterType,
    onChangeElseValue,
    onToggleColumnItem,
    onToggleColumnItem2,
    changeColumnsCount,
    onChangeSingleColumn,
    onChangeElseInputType,
    onChangeReplaceColumn,
    onDeleteSelectedItems,
    onChangeColumnsCount,
    onChangeUpdateType,
    onChangeThenInputType,
    checkForRequiredFields,
    onChangeShiftingMethod,
    onChangePeriodAmount,
    onChangeTimePeriod,
    onChangeColumnName,
    onChangeSelectedColumns,
    onChangeCurrentFormats,
    onChangeOutputFormat,
    onChangeFuzzyMatching,
    onChangeNumberOfSplits,
    onChangeDirection,
    onChangeExpandedNewColumns,
    onChangeBrowserUse
  } = useTransformation({
    setIsOpen,
    setSelectedTransformation,
    columns,
    type: transformationType,
    reset: () => setTransformationType(),
    periodName,
    year
  });

  useEffect(() => {
    if (!initialFilterTypes || initialFilterTypes?.length === 0) return;

    setFilterTypes(cloneDeep(initialFilterTypes));
  }, [initialFilterTypes]);

  const postPatchActions = res => {
    clear();
    setSelectedTransformation(null);
    setAmongIndex();
    close();
  };

  useEffect(() => {
    if (!transformationType || initialFilterTypes?.length > 0) return;

    onSelectFilterType(transformationType);
  }, [transformationType, initialFilterTypes]);

  useHotkeys('Enter,enter', () => {
    if (filterTypes?.length > 0) onSave();
  });

  const handleCancel = () => {
    clear();
    close();
  };

  const onSave = useCallback(
    e => {
      e.preventDefault();

      if (checkForRequiredFields()) return;

      if (selectedTransformation !== null)
        editTransformation(filterTypes, selectedTransformation, {
          selectedIndex: selectedTransformation,
          onSuccess: postPatchActions,
          onError: err => {
            toast.error(extractApiError(err, 'Unable to save this transformation action.'));
          }
        });
      else
        addTransformation(filterTypes, {
          selectedIndex: selectedTransformation,
          onSuccess: postPatchActions,
          onError: err => {
            toast.error(extractApiError(err, 'Unable to add this transformation action.'));
          }
        });
    },
    [elseValue, replaceColumn, filterTypes, selectedTransformation]
  );

  const renderExtraSection = () => {
    if (transformationType === transportationKeys.REPLACE)
      return (
        <Select
          id="grid-table-transformation-replace-column"
          data={columns}
          label="Replace this column"
          value={filterTypes[0]?.replaceColumn}
          rightSection={<IoIosArrowDown />}
          onChange={val => onChangeReplaceColumn(filterTypes[0]?.id, filterTypes[0]?.id, val)}
          placeholder="Select Replace Column"
          required
          clearable
          searchable
          error={filterTypes[0]?.errors?.includes('replace_column')}
          renderOption={props => renderSelectOption({ ...props, hideTooltip: true })}
          maw={296}
        />
      );

    if (
      transformationType === transportationKeys.ABSOLUTE ||
      transformationType === transportationKeys.NEGATIVE
    )
      return (
        <Select
          id="grid-table-transformation-column"
          data={columns}
          label="Column"
          required
          clearable
          searchable
          error={filterTypes[0]?.errors?.includes('column')}
          value={filterTypes[0]?.column}
          rightSection={<IoIosArrowDown />}
          onChange={val => onChangeColumn(filterTypes[0]?.id, filterTypes[0]?.id, val)}
          placeholder="Select Column"
          renderOption={props => renderSelectOption({ ...props, hideTooltip: true })}
          maw={296}
        />
      );

    if (transformationType === transportationKeys.FILL_FORWARD)
      return (
        <>
          <Select
            id="grid-table-transformation-fill-forward-column"
            data={columns}
            label="Column"
            required
            clearable
            searchable
            error={filterTypes[0]?.errors?.includes('column')}
            value={filterTypes[0]?.column}
            rightSection={<IoIosArrowDown />}
            onChange={val => onChangeColumn(filterTypes[0]?.id, filterTypes[0]?.id, val)}
            placeholder="Select Column"
            renderOption={props => renderSelectOption({ ...props, hideTooltip: true })}
            maw={296}
          />

          <RadioGroup
            id="grid-table-transformation-fill-forward-group"
            label="Filling Type"
            required
            value={filterTypes[0]?.operator}
            mt={rem(8)}
            onChange={value => onChangeOperator(filterTypes[0]?.id, filterTypes[0]?.id, value)}
          >
            <Flex gap={10} h="45px" align="center">
              {fillTypesArray.map(fillType => (
                <Radio
                  id={`grid-table-transformation-fill-forward-option-${fillType.value}`}
                  key={fillType.value}
                  value={fillType.value}
                  label={fillType.label}
                />
              ))}
            </Flex>
          </RadioGroup>
        </>
      );

    if (transformationType === transportationKeys.SUBSTITUTE)
      return (
        <Select
          id="grid-table-transformation-substitute-column"
          data={columns}
          label="Column"
          required
          clearable
          searchable
          error={filterTypes[0]?.errors?.includes('replaceColumn')}
          value={filterTypes[0]?.replaceColumn}
          rightSection={<IoIosArrowDown />}
          onChange={val => onChangeReplaceColumn(filterTypes[0]?.id, filterTypes[0]?.id, val)}
          placeholder="Select Column"
          renderOption={props => renderSelectOption({ ...props, hideTooltip: true })}
          maw={296}
        />
      );

    if (transformationType === transportationKeys.DUPLICATE_ROWS)
      return (
        <Flex direction="column" gap={rem(4)} mt={rem(8)}>
          <Text id="grid-table-transformation-duplicate-rows-label" size={rem(14)}>
            Number of Duplications
          </Text>
          <Input
            id="grid-table-transformation-duplicate-rows-input"
            type="number"
            value={filterTypes[0]?.replaceColumn}
            placeholder="Enter number of duplications"
            onChange={e =>
              onChangeReplaceColumn(filterTypes[0]?.id, filterTypes[0]?.id, e.target.value)
            }
          />
        </Flex>
      );

    if (transformationType === transportationKeys.SPLIT_BY_VALUE)
      return (
        <Flex align="center" gap={rem(16)}>
          <Flex direction="column" gap={rem(4)} miw={170}>
            <Select
              id="grid-table-transformation-split-by-value-column"
              data={columns}
              label="Column Name"
              required
              clearable
              searchable
              error={filterTypes[0]?.errors?.includes('replace_column')}
              value={filterTypes[0]?.replaceColumn}
              rightSection={<IoIosArrowDown />}
              onChange={val => onChangeReplaceColumn(filterTypes[0]?.id, filterTypes[0]?.id, val)}
              placeholder="Select Column"
              renderOption={props => renderSelectOption({ ...props, hideTooltip: true })}
              maw={296}
            />
          </Flex>

          <Flex direction="column" gap={rem(4)} mt={rem(4)} miw={170}>
            <Text size={rem(14)} fw={600} mb={2} display="flex">
              Delimiter <Text c="red">*</Text>
            </Text>

            <Input
              id="grid-table-transformation-split-by-value-delimiter"
              value={filterTypes[0]?.then.input}
              required
              error={filterTypes[0]?.errors?.includes('then')}
              placeholder="Enter Delimiter"
              onChange={e =>
                onChangeThen(filterTypes?.[0]?.id, filterTypes?.[0]?.id, e.target.value)
              }
            />
          </Flex>

          <Flex direction="column" gap={rem(4)} mt={rem(4)} miw={170}>
            <Text size={rem(14)} fw={600} mb={2} display="flex">
              Number of Splits ({filterTypes[0]?.numberOfSplits || 0}) <Text c="red">*</Text>
            </Text>

            <Input
              id="grid-table-transformation-split-by-value-splits"
              type="number"
              value={filterTypes[0]?.numberOfSplits}
              required
              error={filterTypes[0]?.errors?.includes('numberOfSplits')}
              placeholder="Enter Number"
              onChange={e =>
                onChangeNumberOfSplits(filterTypes?.[0]?.id, filterTypes?.[0]?.id, e.target.value)
              }
            />
          </Flex>
        </Flex>
      );

    if (transformationType === transportationKeys.JOIN)
      return (
        <Flex align="center" gap={rem(16)}>
          <Flex direction="column" gap={rem(4)} mt={rem(8)}>
            <Text id="grid-table-transformation-join-first-label" size={rem(14)}>
              First Table
            </Text>

            <Input
              id="grid-table-transformation-join-first-input"
              value={filterTypes[0]?.replaceColumn}
              disabled
            />
          </Flex>

          <Flex direction="column" gap={rem(4)} mt={rem(8)}>
            <Text id="grid-table-transformation-join-second-label" size={rem(14)}>
              Second Table
            </Text>

            <SearchSelect
              id="grid-table-transformation-join-second"
              onSelect={(val, dataId) => {
                onChangeElseValue(filterTypes?.[0]?.id, filterTypes?.[0]?.id, dataId);
                onChangeThen(filterTypes?.[0]?.id, filterTypes?.[0]?.id, val);
              }}
              year={year}
              periodName={periodName}
              workflowName={workflowName}
              selectedTableName={filterTypes[0]?.then?.column || filterTypes[0]?.then?.input}
              value={filterTypes[0]?.elseValue?.input || filterTypes[0]?.elseValue?.column}
              placeholder="Select Second Table"
              periodId={periodId}
            />
          </Flex>
        </Flex>
      );

    return <></>;
  };

  return (
    <>
      {!showList && (
        <>
          <Flex align="center" gap="10px" px={rem(16)} style={{ overflow: 'auto' }}>
            <Select
              id="grid-table-transformation-action-select"
              key={transformationType}
              data={transportationsArray.filter(
                transportation =>
                  !filterActions ||
                  transportation.label.toLowerCase().includes(filterActions?.toLowerCase())
              )}
              label="Action"
              value={transformationType}
              rightSection={<IoIosArrowDown />}
              onChange={onChangeTransformationType}
              placeholder="Select"
              renderOption={renderSelectOption}
              maw={296}
              searchable
              autoFocus={!transformationType}
              disabled={disableSelectActionType}
              miw={170}
            />

            {renderExtraSection()}
          </Flex>

          {transformationType && (
            <Flex align="center" gap="8px" mt="8px" mb={rem(8)} px={rem(16)}>
              <Text size={rem(14)} c="#868E96">
                {transformationTooltips[transformationType]}
              </Text>
            </Flex>
          )}
        </>
      )}

      <Flex justify="space-between" direction="column" flex={1} id="transformation_renderer">
        {filterTypes?.length > 0 && (
          <ConditionBody
            periodName={periodName}
            year={year}
            isLockedPeriod={isLockedPeriod}
            extractId={extractId}
            periodId={periodId}
            workflowId={workflowId}
            handleDragEnd={handleDragEnd}
            key={filterTypes.length}
            filterTypes={filterTypes}
            disableSaveBtn={disableSaveBtn}
            selectedItems={selectedItems}
            cuurentItem={filterTypes[0]}
            columns={columns}
            tableName={tableName}
            replaceColumn={replaceColumn}
            onClearColumns={onClearColumns}
            onClearColumns2={onClearColumns2}
            disableMessage={disableMessage}
            tranformationId={tranformationId}
            onSelectItem={onSelectItem}
            addCondition={addCondition}
            onChangeKeep={onChangeKeep}
            onChangeColumnsCount={onChangeColumnsCount}
            onChangeUpdateType={onChangeUpdateType}
            onChangeThen={onChangeThen}
            onChangeInput={onChangeInput}
            onChangeColumn={onChangeColumn}
            onAddFilterType={onAddFilterType}
            onChangeFunction={onChangeFunction}
            onChangeOperator={onChangeOperator}
            onChangeInputType={onChangeInputType}
            onChangeElseValue={onChangeElseValue}
            setTransformationType={setTransformationType}
            changeColumnsCount={changeColumnsCount}
            onToggleColumnItem2={onToggleColumnItem2}
            onChangeSingleColumn={onChangeSingleColumn}
            onChangeElseInputType={onChangeElseInputType}
            onChangeReplaceColumn={onChangeReplaceColumn}
            isChildrenGroup={false}
            onChangeThenInputType={onChangeThenInputType}
            onSelectCondition={onSelectCondition}
            onToggleColumnItem={onToggleColumnItem}
            onDeleteSelectedItems={onDeleteSelectedItems}
            onChangeSelectedColumns={onChangeSelectedColumns}
            onAddListItem={onAddListItem}
            onRemoveListItem={onRemoveListItem}
            isLoadingTransformationPatch={isLoadingTransformationPatch}
            onSave={onSave}
            onChangeShiftingMethod={onChangeShiftingMethod}
            onChangePeriodAmount={onChangePeriodAmount}
            onChangeTimePeriod={onChangeTimePeriod}
            onChangeColumnName={onChangeColumnName}
            selectedTransformation={selectedTransformation}
            onChangeCurrentFormats={onChangeCurrentFormats}
            onChangeOutputFormat={onChangeOutputFormat}
            onChangeFuzzyMatching={onChangeFuzzyMatching}
            onChangeNumberOfSplits={onChangeNumberOfSplits}
            onChangeDirection={onChangeDirection}
            onChangeExpandedNewColumns={onChangeExpandedNewColumns}
            onChangeBrowserUse={onChangeBrowserUse}
            onCancel={handleCancel}
          />
        )}

        {filterTypes.length > 0 && (
          <Flex
            justify="flex-end"
            className="footer"
            pos="sticky"
            bottom={0}
            p={rem(16)}
            gap={rem(16)}
            bg="white"
            sx={{
              boxShadow: 'rgba(0, 0, 0, 0.04) 0px -11px 7px -5px',
              zIndex: 999
            }}
          >
            {disableMessage && (
              <Text
                size={rem(14)}
                c="red"
                maw="65%"
                ta="left"
                display="flex"
                style={{ alignItems: 'center' }}
              >
                {disableMessage}
              </Text>
            )}

            <Button
              id="grid-table-transformation-cancel"
              size="xs"
              variant="outline"
              bd="1px solid #CED4DA"
              c="#141517"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              id="grid-table-transformation-save"
              type="button"
              size="xs"
              onClick={onSave}
              disabled={disableSaveBtn || isLoadingTransformationPatch}
            >
              {isLoadingTransformationPatch ? 'Saving...' : 'Save'}
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export const SkletonWrapper = styled.div`
  width: calc(100% - 460px);
`;
